import { DataLayerEvents } from '@/datalayer/App.datalayer.constants';
import { TabFilter } from '@/features/shared';
import ElasticComponentContext from '@/features/shared/ElasticComponent/ElasticComponentContext';
import FiltersV2 from '@/features/shared/FiltersV2';
import {
  datalayerPush,
  getDataLayerByContentCategoryKeys,
  getDataLayerNumFilters,
  getDataLayerSearchFilters,
  getDataLayerSearchFiltersObject,
} from '@/features/shared/HOCs/withGTM';
import { useIsRevision } from '@/features/shared/Hooks';
import { MuiBox, MuiButton, MuiHidden, MuiTypography } from '@/features/shared/material-ui';
import { ONE_TRUST_COOKIE_ACCEPT_CODE } from '@/hooks';
import { LoadingStatusValues, Theme } from '@/utils/constants';
import { elasticFilters, isLastPageViewSamePath, mapLinkToProps } from '@/utils/helpers';
import { waitForCriteria } from '@/utils/wait-for-criteria';
import { useMediaQuery } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import { ChevronRight } from '@material-ui/icons';
import clsx from 'clsx';
import { isEqual } from 'lodash';
import React, {
  ChangeEvent,
  KeyboardEvent,
  memo,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import TagManager from 'react-gtm-module';
import { FilteredListProps } from '../FilteredList.interface';
import SearchInput from '../SearchInput/SearchInput';
import useStyles from './TopBarContent.styles';
import {
  triggerFilterSearchResult,
  triggerSearchResult,
} from '@/features/FilteredList/TopBarContent/TopBarContent.datalayer';

const TopBarContent = ({
  content,
  presentation,
  labels,
  theme,
  isSearchPage,
  dataLayerSearchAttributesByContentCategory = {},
}: PropsWithChildren<FilteredListProps>) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const { searchHint, searchTip } = presentation;
  const { listTitle, listMenuLink, contentCategoryKeys = [] } = content;
  const { updateQueryParams, queryParams, hideFilters, status, pagination } =
    useContext(ElasticComponentContext);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [open, setOpen] = useState<boolean>(Boolean(anchorEl));
  const [input, setInput] = useState(queryParams.q);
  const classes = useStyles({});
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));
  const [dataLayerId, setDataLayerId] = React.useState('');
  const variantName = (containerRef?.current?.closest('[data-datalayer-variant]') as HTMLDivElement)
    ?.dataset?.datalayerVariant;
  const [eventStartedAtSearchPage, setEventStartedAtSearchPage] = React.useState(isSearchPage);
  const isRevision = useIsRevision();
  const [isFirstLoad, setIsFirstLoad] = React.useState(true);
  const lastPaginationRef = useRef<any>(null);
  const loadSearchResultEvent = useRef(false);

  const handleInput = (e: ChangeEvent<HTMLInputElement>) => {
    setInput(e.target.value);
  };

  const handleApplySearch = React.useCallback(() => {
    updateQueryParams('q', input);
    updateQueryParams('page', '1');

    datalayerPush({
      compRef: containerRef,
      args: {
        searchQuery: input,
        variantName,
      },
      event: 'gtm:custom:search',
    });
    setEventStartedAtSearchPage(isSearchPage);
    setDataLayerId('filtered_list_search');
  }, [input, isSearchPage, updateQueryParams, variantName]);

  React.useEffect(() => {
    if (dataLayerId !== 'filtered_list_search') return;
    if (isFirstLoad && status !== LoadingStatusValues.ready) return;
    if (!isFirstLoad && status !== LoadingStatusValues.loading) return;

    const filters = queryParams?.filters;
    const searchFiltersObject = getDataLayerSearchFiltersObject(filters);
    const searchFilters = getDataLayerSearchFilters(filters);
    const eventAttributes = getDataLayerByContentCategoryKeys({
      contentCategoryKeys,
      dataLayerSearchAttributesByContentCategory,
    });

    if (!eventAttributes?.event) return;
    const dataLayer = {
      ...eventAttributes,
      ...(!!queryParams?.q?.trim() && { search_term: queryParams?.q?.trim() }),
      num_search_result: pagination?.totalResults || 0,
      index_num: String(pagination?.currentPage || 1),
      is_revision: isRevision,
      ...(!!searchFilters && { search_filters: searchFilters }),
      num_search_filters: getDataLayerNumFilters(filters),
      ...searchFiltersObject,
    };

    // TODO: verify last PageView event doesn't have the same URL (page_path) as the current one.
    const shouldNotDelay = isLastPageViewSamePath();
    if (eventStartedAtSearchPage || shouldNotDelay)
      waitForCriteria({
        criteria: () => window?.OnetrustActiveGroups?.includes(ONE_TRUST_COOKIE_ACCEPT_CODE),
        callback: () => TagManager.dataLayer({ dataLayer }),
      });
    if (!eventStartedAtSearchPage && !shouldNotDelay)
      window.dispatchEvent(
        new CustomEvent(DataLayerEvents.DELAYED_PAGE_VIEW_EVENTS, {
          bubbles: true,
          detail: { target: dataLayer },
        }),
      );

    setDataLayerId('');
    setIsFirstLoad(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataLayerId, status, isFirstLoad]);

  React.useEffect(() => {
    setTimeout(() => {
      if (!queryParams?.q) return;
      setEventStartedAtSearchPage(isSearchPage);
      setDataLayerId('filtered_list_search');
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleApplySearchKeyValidation = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      handleApplySearch();
    }
  };

  const areFiltersDifferent = React.useCallback(() => {
    const currentFilters = elasticFilters();
    const newFilters = Object.keys(queryParams?.filters || {}).reduce((prev: any, curr: string) => {
      if (hideFilters?.find((filter: string) => filter === curr)) return { ...prev };

      return { ...prev, [curr]: queryParams?.filters[curr] };
    }, {});

    return !isEqual(currentFilters, newFilters);
  }, [queryParams?.filters, hideFilters]);

  // Mobile Filters
  const handleTabFilterClose = () => {
    if (areFiltersDifferent()) updateQueryParams('page', '1');
  };

  const handleScroll = useCallback(() => {
    const el = anchorEl || popoverRef.current || undefined;
    const elHeight = el?.clientHeight || 0;
    if (!open && elHeight > window.screenTop) {
      setOpen(false);
      setAnchorEl(null);
    }

    return () => setOpen(false);
  }, [anchorEl, open, popoverRef]);

  useEffect(() => {
    if (anchorEl) setOpen(Boolean(anchorEl));
  }, [anchorEl]);

  useEffect(() => {
    document.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      document.removeEventListener('scroll', handleScroll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setInput(queryParams.q || '');
  }, [queryParams.q]);

  const handleGeneralCloseFilters = React.useCallback(() => {
    setEventStartedAtSearchPage(isSearchPage);
    setDataLayerId('apply_filters');
  }, [isSearchPage]);

  const getDatalayerCommons = useCallback(() => {
    const searchTerm = queryParams?.q?.trim();
    const filters = queryParams?.filters;
    const searchFilters = getDataLayerSearchFilters(filters);
    const searchFiltersObject = getDataLayerSearchFiltersObject(filters);

    return {
      ...(!!searchTerm && { search_term: searchTerm }),
      num_search_result: pagination?.totalResults || 0,
      index_num: String(pagination?.currentPage || 1),
      is_revision: isRevision,
      ...(!!searchFilters && { search_filters: searchFilters }),
      num_search_filters: getDataLayerNumFilters(filters),
      ...searchFiltersObject,
    };
  }, [isRevision, pagination, queryParams?.filters, queryParams?.q]);

  React.useEffect(() => {
    if (dataLayerId === 'apply_filters' && status === LoadingStatusValues.ready) {
      triggerFilterSearchResult(getDatalayerCommons());
      setDataLayerId('');
    }
  }, [dataLayerId, getDatalayerCommons, status]);

  useEffect(() => {
    if (dataLayerId === 'filtered_list_search' && status === LoadingStatusValues.ready) {
      // preventing duplicate emission
      if (lastPaginationRef.current === pagination) return;
      lastPaginationRef.current = pagination;

      triggerSearchResult(getDatalayerCommons());
      setDataLayerId('');
    }
  }, [status, dataLayerId, getDatalayerCommons, pagination, isFirstLoad]);

  // TODO: labels?.search_label is not coming
  return (
    <div ref={containerRef} data-datalayer-position="top_bar_content">
      <MuiBox
        display="flex"
        flexDirection={isMobile ? 'column' : 'row'}
        alignItems="flex-start"
        className={classes.root}
      >
        <MuiBox order={1} className={classes.title}>
          <MuiTypography
            variant="h2"
            component="span"
            className={clsx(classes.pageTitle, `${theme}-theme`)}
          >
            {listTitle}
          </MuiTypography>
        </MuiBox>
        <MuiBox order={1} className={classes.spacer} />
        <MuiBox
          display="flex"
          flexDirection={isMobile ? 'column' : 'row'}
          order={isMobile ? 3 : 2}
          className={classes.filtersLinks}
        >
          <div className={classes.filters}>
            {isMobile && (
              <TabFilter
                labels={labels}
                theme={Theme.dark}
                size="small"
                onClose={() => {
                  handleTabFilterClose();
                  handleGeneralCloseFilters();
                }}
              />
            )}
          </div>
          <MuiBox order={isMobile ? 1 : 2} display="flex" className={classes.links}>
            {!!listMenuLink &&
              listMenuLink.length > 0 &&
              listMenuLink.map((link: LinkType) => {
                if (!link?.href) return null;
                const linkProps = mapLinkToProps(link);

                return (
                  <MuiButton
                    key={`link_${link.displayText}`}
                    variant="text"
                    {...linkProps}
                    className={clsx(classes.link, `${theme}-theme`)}
                    endIcon={
                      <MuiHidden mdUp>
                        <ChevronRight />
                      </MuiHidden>
                    }
                  >
                    {link.displayText}
                  </MuiButton>
                );
              })}
          </MuiBox>
        </MuiBox>
        <SearchInput
          value={input}
          onChange={handleInput}
          onKeyPress={handleApplySearchKeyValidation}
          searchHint={searchHint}
          searchTip={searchTip ?? 'Quotation marks (e.g. "example") provide literal search.'}
          theme={theme}
          searchLabel={labels?.search_label ?? 'Search'}
          onSearchButtonClick={handleApplySearch}
        />
      </MuiBox>
      {!isMobile && (
        <FiltersV2
          onClose={() => {
            datalayerPush({
              compRef: containerRef,
              args: {
                params: { filters: { ...queryParams.filters } },
                variantName,
              },
              event: 'gtm:custom:filters',
            });
            handleGeneralCloseFilters();
          }}
        />
      )}
    </div>
  );
};

export default memo(TopBarContent);
